import { ArrowLeft, Bookmark, CalendarDays, MapPin, Share2, Sparkles, Users, Footprints, Car } from "lucide-react";
import { Button } from "./ui/button";
import { CalendarMenu } from "./CalendarMenu";
import { WeatherBadge } from "./WeatherBadge";

function prettyDate(date) {
  try {
    return new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  } catch {
    return date;
  }
}

function TravelSummary({ summary }) {
  if (!summary) return null;
  const Icon = summary.allWalk ? Footprints : Car;
  return (
    <span
      data-testid="travel-summary"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
        summary.allWalk ? "border-border text-foreground" : "border-neon-amber/40 text-foreground"
      }`}
    >
      <Icon size={13} className={summary.allWalk ? "neon-cyan" : "text-amber-400"} />
      {summary.allWalk
        ? `Walkable night · ${summary.totalMin} min on foot total`
        : `${summary.rides} ride${summary.rides > 1 ? "s" : ""} needed · ~${summary.totalMin} min in transit`}
    </span>
  );
}

function HeaderActions({ plan, shared, onShare, onSave, sharing, saving, onPlanOwn }) {
  return (
    <div className="flex flex-wrap gap-3">
      <CalendarMenu plan={plan} />
      {shared ? (
        <Button data-testid="plan-your-own-btn" onClick={onPlanOwn} className="rounded-full cta-gradient text-white font-semibold hover:opacity-90">
          <Sparkles size={16} className="mr-2" /> Plan your own night
        </Button>
      ) : (
        <>
          <Button data-testid="share-itinerary-btn" onClick={onShare} disabled={sharing} variant="outline" className="rounded-full font-semibold">
            <Share2 size={16} className="mr-2" /> {sharing ? "Creating link…" : "Share"}
          </Button>
          <Button data-testid="save-itinerary-btn" onClick={onSave} disabled={saving} className="rounded-full cta-gradient text-white font-semibold hover:opacity-90">
            <Bookmark size={16} className="mr-2" /> {saving ? "Saving…" : "Save night"}
          </Button>
        </>
      )}
    </div>
  );
}

export function ItineraryHeader({ plan, shared, summary, weather, outdoorCount, onBack, ...actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        {shared ? (
          <span
            data-testid="shared-badge"
            className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground"
          >
            <Users size={13} className="neon-cyan" /> Shared plan
          </span>
        ) : (
          <button
            data-testid="back-btn"
            onClick={onBack}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={15} /> New plan
          </button>
        )}
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="itinerary-city">
          A night in {plan.city}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={15} /> {prettyDate(plan.date)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={15} /> {plan.vibe}
          </span>
          <span className="font-display font-bold text-foreground" data-testid="itinerary-budget">{plan.budget}</span>
          <TravelSummary summary={summary} />
        </div>
        <div className="mt-3">
          <WeatherBadge weather={weather} outdoorCount={outdoorCount} />
        </div>
      </div>
      <HeaderActions plan={plan} shared={shared} onPlanOwn={onBack} {...actions} />
    </div>
  );
}
